import Permissions, { usePermissions } from '@app/src/auth/permissions'
import { usePageBreadcrumbs } from '@app/src/components/Navigation/Breadcrumbs/usePageBreadcrumbs'
import useConfigurationFilter from '@app/src/hooks/configurationFilter'
import useFeatureFlagsByAccessor from '@app/src/hooks/useFeatureFlagsByAccessor'
import useScrollWindowToTop from '@app/src/hooks/useScrollWindowToTop'
import ConfigurationsContent from '@app/src/pages/Configurations/ConfigurationsContent'
import { insertIf } from '@app/src/utils/helpersTs'
import paths from '@app/src/wf-constants/paths'
import type { SvgIconComponent } from '@mui/icons-material'
import {
  AssessmentOutlined,
  AttachMoneyOutlined,
  AutoAwesomeOutlined,
  BarChartOutlined,
  BoltOutlined,
  CalculateOutlined,
  ContactMailOutlined,
  ContentPasteSearchOutlined,
  CorporateFareOutlined,
  FlagOutlined,
  FolderOpenOutlined,
  GavelOutlined,
  GroupsOutlined,
  InsightsOutlined,
  LabelOutlined,
  NumbersOutlined,
  PersonOutlined,
  PrivacyTipOutlined,
  QuizOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material'
import { Box } from '@mui/material'
import React from 'react'
import { Helmet } from 'react-helmet'
import { useIntl } from 'react-intl'
import { generatePath, Redirect, Route, Switch, useParams, useRouteMatch } from 'react-router-dom'
import ConfigurationHubLanding from './ConfigurationHubLanding'

export enum CONFIGURATION_PAGE_IDS {
  Automations = 'automations',
  AuditLogs = 'audit-logs',
  Colleagues = 'colleagues',
  Categories = 'categories',
  Users = 'users',
  Organizations = 'organizations',
  OrganizationInfo = 'organizationInfo',
  OrganizationPreferences = 'organizationPreferences',
  AnswerFlags = 'answerFlags',
  FileRepository = 'file-repository',
  RiskScreening = 'risk-screening',
  ClaimsReview = 'claims-review',
  Assessments = 'assessments',
  SuggestedContacts = 'suggested-contacts',
  AssessmentsAdminConfiguration = 'assessment-admin-config',
  QuestionnairesAdminConfiguration = 'questionnaire-admin-config',
  TopicalRiskConfiguration = 'topical-risk-admin-config',
  FlagRecalculationConfiguration = 'flag-recalculation-admin-config',
  BenchmarkAdminConfiguration = 'benchmark-admin-config',
  AiScreening = 'ai-screening',
  SustainabilityProfile = 'sustainability-profile',
}

export interface ConfigurationsParams {
  configurationsPage: CONFIGURATION_PAGE_IDS
  configurationsSubPage: string
}

export type ConfigItems = Array<{
  configId: string
  icon: SvgIconComponent
  permissions?: Permissions | Permissions[]
  featureToggleName?: string
  /** Overrides the default `configurations.<configId>` hub-list label when set. */
  titleMessageId?: string
}>

/** Hub list order matches this array; `useConfigurationFilter` preserves order. */
export const generateConfigItems = (suggestedAnswers: boolean) => [
  {
    configId: CONFIGURATION_PAGE_IDS.Automations,
    icon: BoltOutlined,
    permissions: [Permissions.SOURCING_USER, Permissions.FINANCE_USER, Permissions.INVESTMENT_USER],
  },
  {
    configId: CONFIGURATION_PAGE_IDS.Colleagues,
    icon: GroupsOutlined,
    permissions: [
      Permissions.SOURCING_USER,
      Permissions.FINANCE_USER,
      Permissions.INVESTMENT_USER,
      Permissions.TRANSPARENCY_USER,
    ],
  },
  {
    configId: CONFIGURATION_PAGE_IDS.Categories,
    icon: LabelOutlined,
    permissions: [Permissions.SOURCING_USER, Permissions.INVESTMENT_USER, Permissions.FINANCE_USER],
  },
  {
    configId: CONFIGURATION_PAGE_IDS.AuditLogs,
    icon: ContentPasteSearchOutlined,
    permissions: [
      Permissions.SOURCING_USER,
      Permissions.FINANCE_USER,
      Permissions.INVESTMENT_USER,
      Permissions.TRANSPARENCY_USER,
    ],
  },
  {
    configId: CONFIGURATION_PAGE_IDS.OrganizationInfo,
    icon: PrivacyTipOutlined,
    permissions: [
      Permissions.SOURCING_USER,
      Permissions.FINANCE_USER,
      Permissions.INVESTMENT_USER,
      Permissions.TRANSPARENCY_USER,
    ],
  },
  {
    configId: CONFIGURATION_PAGE_IDS.AnswerFlags,
    icon: FlagOutlined,
    permissions: [Permissions.SOURCING_USER, Permissions.FINANCE_USER, Permissions.INVESTMENT_USER],
    featureToggleName: 'answerFlags',
  },
  {
    configId: CONFIGURATION_PAGE_IDS.OrganizationPreferences,
    icon: AttachMoneyOutlined,
    permissions: [Permissions.SOURCING_USER, Permissions.FINANCE_USER, Permissions.INVESTMENT_USER],
  },
  {
    configId: CONFIGURATION_PAGE_IDS.RiskScreening,
    icon: WarningAmberOutlined,
    permissions: [Permissions.SOURCING_USER, Permissions.FINANCE_USER, Permissions.INVESTMENT_USER],
    titleMessageId: 'navbar.riskScreening',
  },
  {
    configId: CONFIGURATION_PAGE_IDS.Assessments,
    icon: NumbersOutlined,
    permissions: [Permissions.SOURCING_USER, Permissions.FINANCE_USER, Permissions.INVESTMENT_USER],
  },
  {
    configId: CONFIGURATION_PAGE_IDS.AiScreening,
    icon: AutoAwesomeOutlined,
    permissions: [Permissions.SOURCING_USER, Permissions.FINANCE_USER, Permissions.INVESTMENT_USER],
  },
  {
    configId: CONFIGURATION_PAGE_IDS.SustainabilityProfile,
    icon: InsightsOutlined,
    permissions: [Permissions.TRANSPARENCY_USER],
    featureToggleName: 'sustainabilityProfile',
  },
  ...insertIf(suggestedAnswers, {
    configId: CONFIGURATION_PAGE_IDS.FileRepository,
    icon: FolderOpenOutlined,
    permissions: [Permissions.TRANSPARENCY_USER],
  }),
  {
    configId: CONFIGURATION_PAGE_IDS.Users,
    icon: PersonOutlined,
    permissions: [Permissions.WORLDFAVOR_EMPLOYEE_ADMIN],
  },
  {
    configId: CONFIGURATION_PAGE_IDS.Organizations,
    icon: CorporateFareOutlined,
    permissions: [Permissions.WORLDFAVOR_EMPLOYEE_ADMIN],
  },
  {
    configId: CONFIGURATION_PAGE_IDS.ClaimsReview,
    icon: GavelOutlined,
    permissions: [Permissions.WORLDFAVOR_EMPLOYEE_ADMIN],
  },
  {
    configId: CONFIGURATION_PAGE_IDS.SuggestedContacts,
    icon: ContactMailOutlined,
    permissions: [Permissions.WORLDFAVOR_EMPLOYEE_ADMIN],
  },
  {
    configId: CONFIGURATION_PAGE_IDS.QuestionnairesAdminConfiguration,
    icon: QuizOutlined,
    permissions: [Permissions.WORLDFAVOR_EMPLOYEE_ADMIN],
  },
  {
    configId: CONFIGURATION_PAGE_IDS.AssessmentsAdminConfiguration,
    icon: AssessmentOutlined,
    permissions: [Permissions.WORLDFAVOR_EMPLOYEE_ADMIN],
  },
  {
    configId: CONFIGURATION_PAGE_IDS.TopicalRiskConfiguration,
    icon: CalculateOutlined,
    permissions: [Permissions.WORLDFAVOR_EMPLOYEE_ADMIN],
  },
  {
    configId: CONFIGURATION_PAGE_IDS.FlagRecalculationConfiguration,
    icon: FlagOutlined,
    permissions: [Permissions.WORLDFAVOR_EMPLOYEE_ADMIN],
  },
  {
    configId: CONFIGURATION_PAGE_IDS.BenchmarkAdminConfiguration,
    icon: BarChartOutlined,
    permissions: [Permissions.WORLDFAVOR_EMPLOYEE_ADMIN],
  },
]

const ConfigurationsScene: React.FC = () => {
  useScrollWindowToTop()
  const { formatMessage } = useIntl()
  const { hasPermission } = usePermissions()
  const { path } = useRouteMatch()
  const { configurationsPage, configurationsSubPage } = useParams<ConfigurationsParams>()
  const { orgOrAccessorHasFeature: suggestedAnswers } = useFeatureFlagsByAccessor({
    featureToggleName: 'suggested-answers',
    enabled: hasPermission(Permissions.TRANSPARENCY_USER),
    priorityValue: false,
  })
  const filteredConfigItems = useConfigurationFilter(generateConfigItems(suggestedAnswers))
  const allowedConfigIds = new Set(filteredConfigItems.map(item => item.configId))

  // When a sub-page is active, `ConfigurationCollection` owns the breadcrumbs;
  // only set them here on the hub landing so we don't overwrite the child's.
  const configurationsCrumb = {
    label: formatMessage({ id: 'configurations.configurations' }),
    to: paths.configurations,
  }
  usePageBreadcrumbs(configurationsPage ? null : [configurationsCrumb])

  if (!hasPermission(Permissions.ACCESS_SOLUTION_CONFIGURATION)) return null

  if (configurationsPage && !allowedConfigIds.has(configurationsPage)) {
    return <Redirect to={generatePath(path, {})} />
  }

  if (!configurationsPage) {
    return (
      <ConfigurationHubLanding
        hubTitleMessageId="configurations.configurations"
        configItems={filteredConfigItems}
        routePath={path}
      />
    )
  }

  return (
    <>
      <Helmet>
        <title>{formatMessage({ id: 'configurations.configurations' })}</title>
      </Helmet>

      <Box pt={2} flexGrow={1} display="flex" flexDirection="column" minWidth={0}>
        <Switch>
          <Route
            path={decodeURIComponent(
              generatePath(path, {
                configurationsPage: ':configurationsPage?',
                configurationsSubPage: ':configurationsSubPage?',
                activeTabParam: ':activeTabParam?',
                activeTabSubId: ':activeTabSubId?',
              }),
            )}
          >
            <ConfigurationsContent content={configurationsPage} configurationsSubPage={configurationsSubPage} />
          </Route>
        </Switch>
      </Box>
    </>
  )
}

export default ConfigurationsScene
